import { useMemo } from 'react';

import { DataKeyGetterProp, DataKeyType, ValueGetterProp } from '../utils/DataProps';
import { KeyPointMap } from '../utils/KeyPointMap';
import { mapToSortedArray } from '../utils/mapToSortedArray';
import { NumberCoercible } from '../utils/NumberCoercible';
import { PointInfo } from '../utils/PointInfo';
import { XGetterProp, YGetterProp } from '../utils/XYDataProps';

interface UseDataByXValueParams<TDataKeyType extends DataKeyType, TPointType, TDatum, TXValueType = NumberCoercible>
  extends DataKeyGetterProp<TDatum, TDataKeyType>,
    ValueGetterProp<TDatum, TPointType[]>,
    XGetterProp<TPointType, TXValueType>,
    YGetterProp<TPointType> {
  /**
   * The chart data.
   */
  data: readonly Readonly<TDatum>[];
  sortByXValue: boolean;
}

export type DataByXValue<TDataKeyType extends DataKeyType, TPointType, TDatum, TXValueType = NumberCoercible> = [
  TXValueType,
  KeyPointMap<TDataKeyType, TPointType, TDatum, NumberCoercible | undefined>,
][];

/**
 * Returns the given data indexed by x-values.
 */
export const useDataByXValue = <TDataKeyType extends DataKeyType, TPointType, TDatum, TXValueType = NumberCoercible>({
  data,
  dataKeyGetter,
  valueGetter,
  xGetter,
  yGetter,
  sortByXValue,
}: UseDataByXValueParams<TDataKeyType, TPointType, TDatum, TXValueType>): DataByXValue<
  TDataKeyType,
  TPointType,
  TDatum,
  TXValueType
> => {
  return useMemo(() => {
    const dataByXValueMap = data.reduce((acc, series, seriesIndex) => {
      const dataKey = dataKeyGetter(series);
      const points = valueGetter(series);

      points.forEach((point) => {
        const x = xGetter(point);
        const y = yGetter(point);

        const row = acc.get(x) ?? new Map<TDataKeyType, PointInfo<TPointType, TDatum, NumberCoercible | undefined>>();

        row.set(dataKey, { value: y, point, series, seriesIndex });
        acc.set(x, row);
      });

      return acc;
    }, new Map<TXValueType, KeyPointMap<TDataKeyType, TPointType, TDatum, NumberCoercible | undefined>>());

    if (!sortByXValue) {
      return Array.from(dataByXValueMap.entries());
    }
    return mapToSortedArray(dataByXValueMap);
  }, [data, dataKeyGetter, valueGetter, xGetter, yGetter, sortByXValue]);
};
